import {
	COUNTRY_EXCEPTIONS,
	LANG_COUNTRY_MAPPING,
	WEB_LOCALES,
	WEB_LOCALES_EMOJIS,
	WebLocales,
} from '@/constants/web-locales.constant';
import { WebLocale } from '@/enums/web-locale';
import { getAxiosCached } from '@/helpers/axios-helper';
import { useI18n } from '@/helpers/composables/useI18n';
import { transformTranslations } from '@/helpers/language-helpers';
import { FallbackLocale, getActiveLocales, Locale } from '@/helpers/locale-helper';
import { useFilterStore } from '@/stores/pinia/useFilterStore';
import { useMetaStore } from '@/stores/pinia/useMetaStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { Route } from 'vue-router';

export const TTB_LANGUAGES = ['ja', 'ko', 'zh'];
const STREAMING_TRANSLATION_PREFIX = 'WEBAPP_';
const SPORTS_TRANSLATION_PREFIX = 'SPORTS_WEBAPP_';

export const useLanguageStore = defineStore('language', () => {
	const filterStore = useFilterStore();
	const userStore = useUserStore();
	const metaStore = useMetaStore();
	const routingStore = useRoutingStore();

	const i18n = useI18n();

	const webLocale = ref(WebLocale.us);
	const overriddenLanguage = ref('');
	const initialized = ref(false);
	const detectedWebLocale = ref<WebLocale | null>(null);
	const locales = ref<Locale[]>([]);
	const localeMessage = ref<any>({});
	const audioLanguage = ref('');

	const language = computed(() => {
		// If the language is supported in LANG_COUNTRY_MAPPING return that
		if (LANG_COUNTRY_MAPPING[overriddenLanguage.value ?? ''] != null) {
			return overriddenLanguage.value;
		}

		// Otherwise try and get the language from the weblocale
		if (WEB_LOCALES[webLocale.value] != null) {
			return WEB_LOCALES[webLocale.value].substring(0, 2).toLowerCase();
		}

		// if all else fails, default to english
		return 'en';
	});

	const isTTBLanguage = computed(() => TTB_LANGUAGES.includes(language.value));

	const country = computed(() => {
		return webLocale.value && WEB_LOCALES[webLocale.value]
			? WEB_LOCALES[webLocale.value].substr(-2).toUpperCase()
			: 'US';
	});

	const countryEmoji = computed(() =>
		webLocale.value && WEB_LOCALES_EMOJIS[webLocale.value] ? WEB_LOCALES_EMOJIS[webLocale.value] : null
	);

	const locale = computed(() =>
		webLocale.value && WEB_LOCALES[webLocale.value] ? WEB_LOCALES[webLocale.value] : 'en_US'
	);

	const localeObject = computed(
		() => locales.value.filter(locale => locale.webLocale === webLocale.value).find(Boolean) ?? FallbackLocale
	);

	const overriddenLocale = computed(() =>
		overriddenLanguage.value ? `${overriddenLanguage.value}_${country.value}` : locale.value
	);

	const activeLocalesList = computed(() =>
		locales.value.length
			? (locales.value.reduce((arr: { [key: string]: string }, entry) => {
					const { webLocale, full_locale } = entry;
					arr[webLocale] = full_locale;
					return arr;
			  }, {}) as WebLocales)
			: WEB_LOCALES
	);

	function changeWebLocale(newWebLocale: WebLocale) {
		if (!filterStore.getWebLocale(newWebLocale)) {
			// init filter here before setting webLocale for reactivity reason (webLocale is watched everywhere)
			filterStore.initFiltersPerLocale(newWebLocale);
		}

		webLocale.value = newWebLocale;
	}

	function setPersistentWebLocale(newWebLocale: WebLocale) {
		userStore.setPersistedWebLocale(newWebLocale);
	}

	async function setLanguageWhenUserDidNot(language: string) {
		if (userStore.persistedOverriddenLanguage) {
			return;
		}
		setLanguage({ language });
	}

	function setLanguage({ language }: { language: string }) {
		if (!language) {
			return false;
		}
		overriddenLanguage.value = language;
		initialized.value = true;

		if (global.VUE_APP_TESTS_E2E) {
			i18n.setLocaleMessage(language, {});
		} else {
			i18n.setLocaleMessage(language, localeMessage.value);
		}

		i18n.locale.value = language;

		metaStore.setAppDirection({ language });
	}

	function setPreferredAudioLanguage({ language }: { language: string }) {
		audioLanguage.value = language;
	}

	async function fetchTranslationAndSetLanguage({ language, activeRoute }: { language: string; activeRoute: Route }) {
		if (Object.keys(localeMessage.value).length === 0) {
			await fetchTranslation({
				language,
				webLocale: country.value as WebLocale,
				activeRoute,
			});
		}

		if (global.VUE_APP_TESTS_E2E) {
			i18n.setLocaleMessage(language, {});
		} else {
			i18n.setLocaleMessage(language, localeMessage.value);
		}

		i18n.locale.value = language;

		overriddenLanguage.value = language;
		initialized.value = true;
	}

	async function fetchTranslation({
		language,
		webLocale,
		activeRoute,
	}: {
		language: string;
		webLocale?: WebLocale;
		activeRoute: Route | null;
	}) {
		const _webLocale = webLocale?.toUpperCase() ?? country.value;
		const apiUrl = `${JW_CONFIG.API}/translations/${language}_${_webLocale}`;

		const params = { prefix: STREAMING_TRANSLATION_PREFIX, translatable_type: 'ui_string' };

		const fetchPromises = [(await getAxiosCached()).get(apiUrl, { params })];
		if (activeRoute?.name?.startsWith('app.sports') || activeRoute?.name?.startsWith('app.home')) {
			params.prefix = SPORTS_TRANSLATION_PREFIX;
			fetchPromises.push((await getAxiosCached()).get(apiUrl, { params }));
		}

		const [jwApp, jwSports] = (await Promise.all(fetchPromises)).map(({ data }) => transformTranslations(data));

		localeMessage.value = Object.assign(jwApp, jwSports);
	}

	async function fetchSportsTranslation() {
		// check of the sports translations are already loaded
		if (Object.keys(localeMessage.value).some(key => key.startsWith(SPORTS_TRANSLATION_PREFIX))) return;
		const locale = webLocale.value?.toUpperCase() ?? country.value;
		const url = `${JW_CONFIG.API}/translations/${language.value}_${locale}`;
		const params = { prefix: SPORTS_TRANSLATION_PREFIX, translatable_type: 'ui_string' };

		const { data } = await (await getAxiosCached()).get(url, { params });
		localeMessage.value = { ...localeMessage.value, ...transformTranslations(data) };

		i18n.setLocaleMessage(language.value, localeMessage.value);
	}

	async function loadLocales() {
		if (locales.value.length === 0) {
			const weblocales = await getActiveLocales();
			locales.value = weblocales;
		}
	}

	function setDetectedWebLocale(locale: string) {
		const webLocale = COUNTRY_EXCEPTIONS[locale] ? COUNTRY_EXCEPTIONS[locale] : locale;
		detectedWebLocale.value = webLocale as WebLocale;
		if (WEB_LOCALES[webLocale as WebLocale]) {
			changeWebLocale(webLocale as WebLocale);
		}
	}

	function setLanguageFromLocale(webLocale: WebLocale) {
		const isLocaleAvailable = WEB_LOCALES[webLocale];
		if (isLocaleAvailable) {
			const language = isLocaleAvailable.substr(0, 2);
			if (LANG_COUNTRY_MAPPING[language]) {
				overriddenLanguage.value = language;
			}
		}
	}

	function setDetectedLanguage(language: string) {
		if (!LANG_COUNTRY_MAPPING[language]) {
			return (overriddenLanguage.value = 'en');
		}
		overriddenLanguage.value = language;
	}

	function setWebLocale(newWebLocale: WebLocale) {
		changeWebLocale(newWebLocale);
		setPersistentWebLocale(newWebLocale);
	}

	watch(overriddenLanguage, async language => {
		if (language == null) return;

		await fetchTranslation({ language, activeRoute: routingStore.activeRoute });
		setLanguage({ language });
	});

	return {
		webLocale,
		overriddenLanguage,
		initialized,
		detectedWebLocale,
		locales,
		localeMessage,
		audioLanguage,

		language,
		isTTBLanguage,
		country,
		countryEmoji,
		locale,
		localeObject,
		overriddenLocale,
		activeLocalesList,
		changeWebLocale,
		setPersistentWebLocale,
		setLanguageWhenUserDidNot,
		setLanguage,
		setPreferredAudioLanguage,
		fetchTranslationAndSetLanguage,
		fetchTranslation,
		fetchSportsTranslation,
		loadLocales,
		setDetectedWebLocale,
		setLanguageFromLocale,
		setDetectedLanguage,
		setWebLocale,
	};
});
